<template>
  <div>
    <b-sidebar
      id="sidebar-consultant-certificate"
      sidebar-class="sidebar-lg"
      :visible="consultantCertificateSidebar.visible"
      bg-variant="white"
      shadow
      backdrop
      no-header
      right
      @shown="onShow"
      @change="val => mutateConsultantCertificateSidebar({ visible: val })"
      @hidden="clear"
    >
      <template #default="{ hide }">

        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
        >
          <h4>Solicitação de certificado</h4>
          <div>
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"
            />
          </div>
        </div>

        <!-- Loading do formulário -->
        <div
          v-if="loading.form"
          class="justify-content-center align-items-center"
        >
          <b-spinner
            class="my-3 mx-auto d-block"
            variant="primary"
          />
        </div>

        <!-- Form -->
        <b-form
          v-else
          class="p-2"
          @submit.prevent="onSubmit"
          @reset.prevent="resetForm"
        >
          <b-row>
            <!-- PN -->
            <b-col cols="12">
              <b-form-group
                label="PN"
                label-for="certificate-consultant-pn"
                class="mt-1"
              >
              <v-select
                id="certificate-consultant-pn"
                v-model="consultant"
                :reduce="c => c.id"
                :options="consultantOptions"
                :loading="loading.consultants"
                :disabled="loading.consultants || consultantDisabled"
                label="name"
                placeholder="Selecione o consultor"
                :class="getSelectErrorClass(v$.consultant.$error)"
                @input="v$.consultant.$touch()"
              />
                <div
                  class="invalid-feedback"
                  :class="{ 'd-block': v$.consultant.$error }"
                >
                  <span v-if="v$.consultant.required.$invalid">
                    Você deve informar um consultor.
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <!-- Tipo de curso -->
            <b-col cols="12">
              <b-form-group
                label="Tipo de curso"
                label-for="certificate-type"
                class="mt-1"
              >
                <v-select
                  id="certificate-type"
                  v-model="certificateType"
                  :reduce="t => t.id"
                  :options="certificateTypeOptions"
                  :loading="loading.certificateTypes"
                  :disabled="loading.certificateTypes || isEditingCertificate"
                  label="name"
                  placeholder="Selecione o tipo de curso"
                  :class="getSelectErrorClass(v$.certificateType.$error)"
                  @input="v$.certificateType.$touch()"
                />
                <div
                  class="invalid-feedback"
                  :class="{ 'd-block': v$.certificateType.$error }"
                >
                  <span v-if="v$.certificateType.required.$invalid">
                    Você deve informar o tipo de curso.
                  </span>
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Situação da solicitação -->
          <b-row v-if="certificate">
            <b-col cols="12" class="mt-1">
              <div class="mb-50">
                <strong>Status:</strong>
                <b-badge :variant="statusVariant(certificate.status)">
                  {{ statusLabel(certificate.status) }}
                </b-badge>
              </div>
              <div v-if="certificate.origin_label" class="mb-50">
                <strong>Origem:</strong> {{ certificate.origin_label }}
              </div>
            </b-col>
          </b-row>

          <!-- Motivo (obrigatório para reprovar) -->
          <b-row v-if="canReview">
            <b-col cols="12">
              <b-form-group
                label="Motivo da reprovação"
                label-for="certificate-reject-reason"
                class="mt-1"
              >
                <b-form-textarea
                  id="certificate-reject-reason"
                  v-model="reason"
                  rows="3"
                  maxlength="1000"
                  placeholder="Obrigatório para reprovar"
                  :state="reasonError ? false : null"
                  @input="reasonError = false"
                />
                <div
                  class="invalid-feedback"
                  :class="{ 'd-block': reasonError }"
                >
                  Informe o motivo da reprovação.
                </div>
              </b-form-group>
            </b-col>
          </b-row>

          <div class="d-flex mt-2 justify-content-between">
            <template v-if="!isEditingCertificate">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="outline-secondary"
                type="reset"
                class="certificate-buttons"
              >
                Cancelar
              </b-button>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="primary"
                type="submit"
                class="certificate-buttons"
              >
                {{ saving ? "Aguarde..." : "Salvar e enviar para análise" }}
              </b-button>
            </template>
            <template v-else-if="canReview">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="outline-danger"
                class="certificate-buttons"
                @click="reject"
              >
                {{ saving ? "Aguarde..." : "Reprovar" }}
              </b-button>

              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                :disabled="saving"
                variant="success"
                class="certificate-buttons"
                @click="approve"
              >
                {{ saving ? "Aguarde..." : "Aprovar" }}
              </b-button>
            </template>
            <template v-else>
              <b-button
                variant="outline-secondary"
                class="certificate-buttons"
                type="reset"
              >
                Fechar
              </b-button>
            </template>
          </div>

          <!-- Histórico da solicitação -->
          <div v-if="certificate" class="mt-2">
            <h5>Histórico</h5>
            <ul class="list-unstyled mb-0">
              <li
                v-for="history in certificate.histories"
                :key="history.id"
                class="mb-1"
              >
                <div>
                  <strong>{{ history.to_status_label }}</strong>
                  <span v-if="history.from_status_label" class="text-muted">
                    (antes: {{ history.from_status_label }})
                  </span>
                </div>
                <small class="text-muted">
                  {{ history.created_at_formatted }} · {{ historyUserName(history) }} ·
                  {{ history.actor_role_label }}<span v-if="history.user_role"> ({{ history.user_role.name }})</span>
                </small>
                <div v-if="history.reason">
                  <small>Motivo: {{ history.reason }}</small>
                </div>
              </li>

              <!-- Solicitação anterior ao histórico por etapa: só há solicitante e aprovador -->
              <template v-if="!certificate.histories || !certificate.histories.length">
                <li class="mb-1">
                  <strong>Solicitado</strong>
                  <div>
                    <small class="text-muted">
                      {{ certificate.created_at | dateTime }} · {{ certificate.requested_by ? certificate.requested_by.name : "-" }}
                    </small>
                  </div>
                </li>
                <li v-if="certificate.approved_by" class="mb-1">
                  <strong>Aprovado</strong>
                  <div>
                    <small class="text-muted">
                      {{ certificate.approved_at_formatted }} · {{ certificate.approved_by.name }}
                    </small>
                  </div>
                </li>
              </template>
            </ul>
          </div>
        </b-form>
      </template>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormTextarea,
  BButton,
  BBadge,
  BCol,
  BRow,
  BSpinner,
} from "bootstrap-vue";
import { mapActions, mapMutations, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import Ripple from "vue-ripple-directive";
import vSelect from "vue-select";
import * as types from "../store/types";
import * as sharedTypes from "@/modules/shared/store/types";
import { getVueSelectErrorClass } from "@/helpers/validators";
import { certificateStatusLabel, certificateStatusVariants } from "@/constants/certificates_status";
import _ from "lodash";

export default {
  components: {
    BButton,
    BBadge,
    BSidebar,
    BForm,
    BFormGroup,
    BFormTextarea,
    BCol,
    BRow,
    BSpinner,
    vSelect,
  },
  directives: {
    Ripple,
  },
  props: {
    callback: Function,
  },
  data() {
    return {
      loading: {
        form: false,
        certificateTypes: false,
        consultants: false,
      },
      consultantDisabled: true,
      consultant: undefined,
      certificateType: undefined,
      certificate: null,
      reason: "",
      reasonError: false,
      saving: false,
    };
  },
  setup() {
    return {
      toast: useToast(),
      v$: useVuelidate(),
    };
  },
  validations() {
    return {
      consultant: { required },
      certificateType: { required },
    };
  },
  computed: {
    ...mapGetters({
      consultantCertificateSidebar: types.CONSULTANT_CERTIFICATE_SIDEBAR,
      certificateTypes: types.CONSULTANT_CERTIFICATE_TYPES,
      consultants: sharedTypes.CONSULTANTS,
    }),

    availableActions() {
      return (this.certificate && this.certificate.available_actions) || {};
    },

    canReview() {
      return !!(this.availableActions.leader_review || this.availableActions.final_review);
    },

    // O PN vem travado e nem sempre está na lista de consultores (ex.: o próprio PN): garante o nome.
    consultantOptions() {
      const options = this.consultants || [];
      const { consultant_id: id, consultant_name: name } = this.consultantCertificateSidebar;
      if (!id || !name || options.some((c) => Number(c.id) === Number(id))) {
        return options;
      }
      return [{ id: Number(id), name }, ...options];
    },

    certificateTypeOptions() {
      return this.certificateTypes || [];
    },

    isEditingCertificate() {
      return !!this.consultantCertificateSidebar.id;
    },
  },

  mounted() {
    this.loading.certificateTypes = true;
    this.getCertificateTypes()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text:
              "Ocorreu um erro ao carregar os tipos de certificado. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.certificateTypes = false;
      });

    this.loading.consultants = true;
    this.getConsultants()
      .catch(() => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text:
              "Ocorreu um erro ao carregar os consultores para seleção. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        this.loading.consultants = false;
      });
  },
  methods: {
    ...mapMutations({
      mutateConsultantCertificateSidebar:
        types.MUTATE_CONSULTANT_CERTIFICATE_SIDEBAR,
    }),
    ...mapActions({
      saveConsultantCertificate: types.SAVE_CONSULTANT_CERTIFICATE,
      updateConsultantCertificate: types.UPDATE_CONSULTANT_CERTIFICATE,
      leaderReviewConsultantCertificate: types.LEADER_REVIEW_CONSULTANT_CERTIFICATE,
      getCertificateTypes: types.GET_CONSULTANT_CERTIFICATE_TYPES,
      getConsultants: sharedTypes.GET_CONSULTANTS,
      getConsultantCertificate: types.GET_CONSULTANT_CERTIFICATE,
    }),

    onShow() {
      this.saving = false;
      this.certificate = null;
      this.reason = "";
      this.reasonError = false;

      // Recarrega os tipos de curso para refletir edições recentes (ex.: nome alterado),
      // evitando que o dropdown mostre o nome antigo na aprovação.
      this.loading.certificateTypes = true;
      this.getCertificateTypes()
        .catch(() => {})
        .finally(() => {
          this.loading.certificateTypes = false;
        });

      if (this.consultantCertificateSidebar.consultant_id) {
        this.consultant = this.consultantCertificateSidebar.consultant_id;
        this.consultantDisabled = true;
      } else {
        this.consultant = undefined;
        this.consultantDisabled = false;
      }

      if (this.consultantCertificateSidebar.id) {
        this.loading.form = true;
        this.getConsultantCertificate(this.consultantCertificateSidebar.id)
          .then((resp) => {
            const data = resp.data;
            this.consultant = Number(data.consultant_id);
            this.consultantDisabled = true;
            this.certificateType = Number(data.certificate_type_id);
            this.certificate = data;
          })
          .catch(() => {
            this.toast({
              component: ToastificationContent,
              props: {
                title: "Oops!",
                text:
                  "Ocorreu um erro ao carregar os dados da solicitação de certificado. Entre em contato com o setor de TI.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          })
          .finally(() => {
            this.loading.form = false;
          });
      } else {
        this.certificateType = undefined;
      }
    },

    statusLabel(status) {
      return certificateStatusLabel(status);
    },

    statusVariant(status) {
      return certificateStatusVariants[status] || "secondary";
    },

    historyUserName(history) {
      return history.user ? history.user.name : "-";
    },

    approve() {
      this.review("approve");
    },

    reject() {
      if (!this.reason || !this.reason.trim()) {
        this.reasonError = true;
        return;
      }
      this.review("reject");
    },

    // A etapa vem do backend: o líder direto decide a etapa dele; quem tem "Aprovar certificado", a final.
    review(decision) {
      this.saving = true;

      const id = this.consultantCertificateSidebar.id;
      const reason = decision === "reject" ? this.reason.trim() : undefined;
      const request = this.availableActions.leader_review
        ? this.leaderReviewConsultantCertificate({ id, decision, reason })
        : this.updateConsultantCertificate({
          id,
          status: decision === "approve" ? "approved" : "rejected",
          reason,
        });

      request
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso!",
              text: decision === "approve"
                ? "Solicitação de certificado aprovada com sucesso!"
                : "Solicitação de certificado reprovada com sucesso!",
              icon: "CheckIcon",
              variant: "success",
            },
          });
          this.finish();
        })
        .catch((err) => this.showError(err))
        .finally(() => {
          this.saving = false;
        });
    },

    async onSubmit() {
      const isFormCorrect = await this.v$.$validate();
      if (!isFormCorrect) return;

      this.saving = true;

      this.saveConsultantCertificate({
        consultant_id: this.consultant,
        certificate_type_id: this.certificateType,
      })
        .then(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Sucesso!",
              text: "Solicitação de certificado salva e enviada para análise com sucesso!",
              icon: "CheckIcon",
              variant: "success",
            },
          });
          this.finish();
        })
        .catch((err) => this.showError(err))
        .finally(() => {
          this.saving = false;
        });
    },

    finish() {
      this.mutateConsultantCertificateSidebar({ visible: false });

      if (this.consultantCertificateSidebar.saveAction) {
        this.consultantCertificateSidebar.saveAction();
      }
    },

    // 400 (solicitação já aberta) e 422 (regra do fluxo) trazem a mensagem para o usuário.
    showError(err) {
      const status = _.get(err, "response.status");
      const data = _.get(err, "response.data") || {};
      const message = data.error || data.message;

      if ((status === 400 || status === 422) && message) {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: message,
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }

      this.toast({
        component: ToastificationContent,
        props: {
          title: "Oops!",
          text:
            "Ocorreu um erro ao salvar a solicitação de certificado. Entre em contato com o setor de TI.",
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    },

    getSelectErrorClass(thereIsError) {
      return getVueSelectErrorClass(thereIsError);
    },

    clear() {
      this.consultant = undefined;
      this.consultantDisabled = true;
      this.certificateType = undefined;
      this.certificate = null;
      this.reason = "";
      this.reasonError = false;

      this.mutateConsultantCertificateSidebar({
        id: undefined,
        consultant_id: undefined,
        consultant_name: undefined,
      });

      this.v$.$reset();
    },
    resetForm() {
      this.mutateConsultantCertificateSidebar({ visible: false });
      this.clear();
    },
  },
};
</script>

<style lang="scss">
.certificate-buttons {
  width: 11rem;
}
</style>
